import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteProps, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";
import Api from "../../../../../core/services/api/api";

interface ExternalCustomerOption {
    customerNo: string;
    internalId: number;
    name: string;
}

interface ExternalCustomerAutocompleteProps<M extends boolean = false> extends Omit<AutocompleteProps<ExternalCustomerOption, M, false, false>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;

    [key: string]: any;
}

interface CustomerSearchResponse {
    data?: {
        items: ExternalCustomerOption[],
    }
}

const ExternalCustomerAutocomplete = <M extends boolean = false, >({
                                                                       textFieldProps = {},
                                                                       form = false,
                                                                       name = undefined,
                                                                       required = false,
                                                                       setLoadingOptions: setLoadingOptionsProps,
                                                                       ...props
                                                                   }: ExternalCustomerAutocompleteProps<M>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches customer options based on search keywords
     * Handles loading state and component unmounting
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string
     * @returns Array of matching customer options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken, keywords?: string) => {
        setLoadingOptions(true);
        try {
            const response = await Api.externalCustomerAutoFill({
                keyword: keywords,
                pagination: {pageSize: 20, currentPage: 1}
            }, cancelToken as any) as CustomerSearchResponse;
            if (!isMounted())
                return [];
            return response?.data?.items ?? [];
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [isMounted]);

    /**
     * Determines if two customer options are equal based on their IDs
     */
    const isOptionEqualToValue = useCallback((option: ExternalCustomerOption, value: ExternalCustomerOption | null) =>
        option.internalId === value?.internalId, []);

    /**
     * Extracts the display label from a customer option
     */
    const getOptionLabel = useCallback((option: ExternalCustomerOption) => (option?.name ?? "").concat(option?.customerNo ? ` (${option?.customerNo})` : ""), []);

    /**
     * Gets the unique key for a customer option
     */
    const getOptionKey = useCallback((option: ExternalCustomerOption) => option?.internalId ?? "", []);

    // GetOptions will populate an initial empty array
    const defaultOptions = useMemo(() => [], []);

    return (
        <AutocompleteBase<ExternalCustomerOption, M>
            keywordsBased
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            options={defaultOptions}
            clearOnBlur={false}
            {...props}
        />
    );
};

export default ExternalCustomerAutocomplete;